import React, { FC } from 'react';

import LaunchIcon from '@mui/icons-material/Launch';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { clientAttributes } from 'services/types/client';
import { TemplateResponse } from 'services/types/message';
import textToReactNodes from 'utils/textToReactNodes';

const getPreviewText = (text: string, bodyVariableExamples: string[]): string => {
  let previewText = text;
  bodyVariableExamples.forEach((example, index) => {
    previewText = previewText.replace(`{{${index + 1}}}`, example || `{{${index + 1}}}`);
  });
  return previewText;
};

const FormatPreviewText: FC<{ text: React.ReactNode[] }> = ({ text }) => {
  return (
    <>
      {text.map((node) => {
        const textPart = node?.toString();
        if (typeof textPart === 'string' && !textPart.includes('Object')) {
          return textPart.split(' ').map((word) => {
            if (word.startsWith('client.')) {
              return (
                <>
                  <span key={word} style={{ backgroundColor: '#9EDDFF', padding: '0 2px', borderRadius: '4px' }}>
                    {clientAttributes.find((attribute) => attribute.value === word)?.title || word}
                  </span>{' '}
                </>
              );
            }
            return `${word} `;
          });
        }
        return node;
      })}
    </>
  );
};

const PreviewHeader: FC<{
  headerText: string | null;
  headerFormat: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  headerFileUrl: string | null;
}> = ({ headerText, headerFormat, headerFileUrl }) => {
  if (headerFormat === 'TEXT' && headerText) {
    return (
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {headerText}
      </Typography>
    );
  }

  if (headerFormat === 'IMAGE' && headerFileUrl) {
    return (
      <Box>
        <img src={headerFileUrl} alt="Header" style={{ maxWidth: '200px' }} />
      </Box>
    );
  }

  return null;
};

const PreviewBody: FC<{ bodyText: string; bodyVariableExamples: string[] | null }> = ({
  bodyText,
  bodyVariableExamples,
}) => {
  let previewText = bodyText;
  if (bodyVariableExamples) {
    previewText = getPreviewText(bodyText, bodyVariableExamples);
  }

  return (
    <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
      <FormatPreviewText text={textToReactNodes(previewText)} />
    </Typography>
  );
};

const PreviewFooter: FC<{ footerText: string }> = ({ footerText }) => {
  if (!footerText) return null;

  return (
    <Typography variant="body2" color="text.secondary">
      {footerText}
    </Typography>
  );
};

const PreviewButtons: FC<{ buttons: TemplateResponse[number]['components'][number]['buttons'] }> = ({ buttons }) => {
  return (
    <Stack spacing={1} mt={2}>
      {buttons?.map(
        (button, index) =>
          button.text && (
            <Button
              key={`${index + 1}`}
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
              }}
              href={button.type === 'URL' ? button.url : undefined}
              startIcon={button.type === 'URL' ? <LaunchIcon /> : undefined}
            >
              {button.text}
            </Button>
          )
      )}
    </Stack>
  );
};

interface Props {
  cardTitle?: string;
  headerEnabled: boolean;
  headerText: string | null;
  headerFormat: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | null;
  headerFileUrl: string | null;
  bodyText: string;
  bodyVariableExamples: string[] | null;
  footerEnabled: boolean;
  footerText: string | null;
  buttons: TemplateResponse[number]['components'][number]['buttons'] | null;
  description?: React.ReactNode | null;
}

const PreviewTemplate: FC<Props> = ({
  cardTitle,
  headerEnabled,
  headerText,
  headerFormat,
  headerFileUrl,
  bodyText,
  bodyVariableExamples,
  footerEnabled,
  footerText,
  buttons,
  description,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        position: 'sticky',
        top: 16,
      }}
    >
      <Typography variant="h6" gutterBottom>
        {cardTitle}
      </Typography>
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          borderRadius: 2,
          p: 2,
          minHeight: 200,
        }}
      >
        {headerEnabled && headerFormat && (
          <PreviewHeader headerText={headerText} headerFormat={headerFormat} headerFileUrl={headerFileUrl} />
        )}
        <PreviewBody bodyText={bodyText} bodyVariableExamples={bodyVariableExamples} />
        {footerEnabled && footerText && <PreviewFooter footerText={footerText} />}
        {buttons && buttons.length > 0 && <PreviewButtons buttons={buttons} />}
      </Box>
      {description}
    </Paper>
  );
};

PreviewTemplate.defaultProps = {
  cardTitle: 'Vista Previa',
  description: null,
};

export default PreviewTemplate;
